import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';

const API_BASE = 'https://api.getnovala.com/api/v1';
const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Automatically attach token to every request
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// If token expires redirect to login
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const url: string = (error.config?.url as string) || '';
    const isAuthRequest =
      url.includes('/auth/login') || url.includes('/auth/register');
    if (error.response?.status === 401 && !isAuthRequest) {
      localStorage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

type Id = string | number;
type Dict = Record<string, unknown>;

// ── Auth ──────────────────────────────────────────────────────────────────
export const login = (email: string, password: string): Promise<AxiosResponse> =>
  api.post('/auth/login', { email, password });

export const register = (
  email: string,
  password: string,
  fullName: string,
  company?: string
): Promise<AxiosResponse> =>
  api.post('/auth/register', {
    email,
    password,
    full_name: fullName,
    company,
  });

// ── Documents ─────────────────────────────────────────────────────────────
export const getDocuments = (): Promise<AxiosResponse> =>
  api.get('/documents/');

export const uploadDocument = (file: File): Promise<AxiosResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteDocument = (id: Id): Promise<AxiosResponse> =>
  api.delete(`/documents/${id}`);

export const updateDocument = (id: Id, data: Dict): Promise<AxiosResponse> =>
  api.patch(`/documents/${id}`, data);

// ── Transactions ──────────────────────────────────────────────────────────
export const getTransactions = (params?: Dict): Promise<AxiosResponse> =>
  api.get('/transactions/', { params });

export const updateTransaction = (id: Id, data: Dict): Promise<AxiosResponse> =>
  api.patch(`/transactions/${id}`, data);

export const deleteTransaction = (id: Id): Promise<AxiosResponse> =>
  api.delete(`/transactions/${id}`);

export const categorizeTransaction = (id: Id): Promise<AxiosResponse> =>
  api.post(`/transactions/${id}/categorize`);

// ── Budgets ───────────────────────────────────────────────────────────────
export const getBudgets = (): Promise<AxiosResponse> => api.get('/budgets/');

export const createBudget = (data: Dict): Promise<AxiosResponse> =>
  api.post('/budgets/', data);

export const deleteBudget = (category: string): Promise<AxiosResponse> =>
  api.delete(`/budgets/${encodeURIComponent(category)}`);

// ── Invoices ──────────────────────────────────────────────────────────────
export const getInvoices           = (): Promise<AxiosResponse>                 => api.get('/invoices/');
export const createInvoice         = (data: Dict): Promise<AxiosResponse>       => api.post('/invoices/', data);
export const getInvoice            = (id: Id): Promise<AxiosResponse>           => api.get(`/invoices/${id}`);
export const updateInvoice         = (id: Id, data: Dict): Promise<AxiosResponse> => api.patch(`/invoices/${id}`, data);
export const deleteInvoice         = (id: Id): Promise<AxiosResponse>           => api.delete(`/invoices/${id}`);
export const markInvoicePaid       = (id: Id): Promise<AxiosResponse>           => api.post(`/invoices/${id}/mark-paid`);
export const autoCreateInvoice     = (docId: Id): Promise<AxiosResponse>        => api.post(`/invoices/auto-create/${docId}`);
export const autoCreateAllInvoices = (): Promise<AxiosResponse>                 => api.post('/invoices/auto-create-all');

// ── Team ──────────────────────────────────────────────────────────────────
export const getTeamMembers   = (): Promise<AxiosResponse>                    => api.get('/team/members');
export const inviteTeamMember = (data: Dict): Promise<AxiosResponse>          => api.post('/team/invite', data);
export const updateTeamMember = (id: Id, data: Dict): Promise<AxiosResponse>  => api.patch(`/team/${id}/role`, data);
export const removeTeamMember = (id: Id): Promise<AxiosResponse>              => api.delete(`/team/${id}`);

// ── Analytics ─────────────────────────────────────────────────────────────
export const getDashboardStats = (): Promise<AxiosResponse> => api.get('/analytics/dashboard');
export const getCashFlow       = (): Promise<AxiosResponse> => api.get('/analytics/cash-flow');

// ── Agents ────────────────────────────────────────────────────────────────
export const agentChat = (
  messages: unknown[],
  provider: string = 'openai'
): Promise<AxiosResponse> =>
  api.post('/agents/chat', { messages, provider });

export const getAgentLogs = (): Promise<AxiosResponse> => api.get('/agents/logs');

// ── Integrations ──────────────────────────────────────────────────────────
export const getIntegrations       = (): Promise<AxiosResponse>              => api.get('/integrations/');
export const connectIntegration    = (provider: string): Promise<AxiosResponse> => api.post(`/integrations/${provider}/connect`);
export const disconnectIntegration = (provider: string): Promise<AxiosResponse> => api.post(`/integrations/${provider}/disconnect`);

// ── Company Profile ───────────────────────────────────────────────────────
export const getCompanyProfile   = (): Promise<AxiosResponse>              => api.get('/company/profile');
export const saveCompanyProfile  = (data: Dict): Promise<AxiosResponse>    => api.post('/company/profile', data);
export const patchCompanyProfile = (data: Dict): Promise<AxiosResponse>    => api.patch('/company/profile', data);

// ── Tax Reports ───────────────────────────────────────────────────────────
export const getTaxReports   = (): Promise<AxiosResponse>              => api.get('/tax-reports/');
export const getTaxReport    = (id: Id): Promise<AxiosResponse>        => api.get(`/tax-reports/${id}`);
export const createTaxReport = (data: Dict): Promise<AxiosResponse>    => api.post('/tax-reports/', data);
export const deleteTaxReport = (id: Id): Promise<AxiosResponse>        => api.delete(`/tax-reports/${id}`);

// ── What-If Scenarios ─────────────────────────────────────────────────────
export const getScenarios   = (): Promise<AxiosResponse>              => api.get('/scenarios/');
export const getScenario    = (id: Id): Promise<AxiosResponse>        => api.get(`/scenarios/${id}`);
export const createScenario = (data: Dict): Promise<AxiosResponse>    => api.post('/scenarios/', data);
export const deleteScenario = (id: Id): Promise<AxiosResponse>        => api.delete(`/scenarios/${id}`);

// ── AI ────────────────────────────────────────────────────────────────────
export const askAI = (
  question: string,
  sessionId?: string | null,
  page?: string,
  pageContext?: unknown,
  mode?: string
): Promise<AxiosResponse> =>
  api.post('/ai/ask', {
    question,
    session_id:   sessionId   || null,
    page:         page        || 'dashboard',
    mode:         mode        || page || 'dashboard',
    page_context: pageContext || null,
  });

export const getAIHistory = (sessionId?: string): Promise<AxiosResponse> =>
  api.get('/ai/history', { params: { session_id: sessionId } });

export const clearAIHistory   = (): Promise<AxiosResponse> => api.delete('/ai/history');
export const getAIMemory      = (): Promise<AxiosResponse> => api.get('/ai/memory');
export const clearAIMemory    = (): Promise<AxiosResponse> => api.delete('/ai/memory');
export const refreshAIMemory  = (): Promise<AxiosResponse> => api.post('/ai/memory/refresh');
export const getAIInsights    = (): Promise<AxiosResponse> => api.get('/ai/insights');
export const getAISuggestions = (page?: string): Promise<AxiosResponse> =>
  api.get('/ai/suggestions', { params: { page } });

// ── Snapshots ─────────────────────────────────────────────────────────────
export const getLatestSnapshot   = (): Promise<AxiosResponse>                  => api.get('/snapshots/latest');
export const getSnapshotTimeline = (months?: number): Promise<AxiosResponse>   => api.get('/snapshots/', { params: { months: months || 12 } });
export const generateSnapshot    = (): Promise<AxiosResponse>                  => api.post('/snapshots/generate');

// ── Preferences ───────────────────────────────────────────────────────────
export const getPreferences     = (): Promise<AxiosResponse>                       => api.get('/preferences/');
export const setPreference      = (key: string, value: unknown): Promise<AxiosResponse> => api.post('/preferences/', { key, value });
export const setPreferencesBulk = (data: Dict): Promise<AxiosResponse>             => api.post('/preferences/bulk', data);
export const deletePreference   = (key: string): Promise<AxiosResponse>            => api.delete(`/preferences/${key}`);

export const getDailyBriefing = (): Promise<AxiosResponse> => api.get('/analytics/briefing');

// ── Financial Reports ─────────────────────────────────────────────────────
export const getPLReport       = (params?: Dict): Promise<AxiosResponse> => api.get('/analytics/pl-report', { params });
export const getBalanceSheet   = (params?: Dict): Promise<AxiosResponse> => api.get('/analytics/balance-sheet', { params });
export const getCashFlowReport = (params?: Dict): Promise<AxiosResponse> => api.get('/analytics/cash-flow-report', { params });

// ── Bills ─────────────────────────────────────────────────────────────────
export const getBills     = (): Promise<AxiosResponse>              => api.get('/bills/');
export const createBill   = (data: Dict): Promise<AxiosResponse>    => api.post('/bills/', data);
export const markBillPaid = (id: Id): Promise<AxiosResponse>        => api.patch(`/bills/${id}/pay`);
export const deleteBill   = (id: Id): Promise<AxiosResponse>        => api.delete(`/bills/${id}`);

export default api;